/**
 * src/lib/api.ts — the HTTP seam. Base URL, `Authorization: Bearer` injection, JSON parsing, and
 * the source's error handling reproduced exactly.
 *
 * Source: `D:/WCS/latex2html-french-trad/src/lib/api-config.ts` (whole file),
 * `src/lib/auth.ts` (whole file), and the two identical `getAuthHeaders()` helpers at
 * `src/hooks/use-database.ts:8-14` and `src/hooks/use-admin.ts:9-15`.
 * Spec: `docs/spec/06-frontend-components.md` §2.2, §2.3, §2.4, §3 (opening paragraph).
 *
 * This file merges four source modules into one because the port's file budget for this layer is
 * four files. Nothing about the request/response behaviour changes; `apiUrl()` remains the single
 * indirection point spec §2.3 asks for.
 *
 * ═══ DF-22 rank 5 — ERROR SWALLOWING IS THE CONTRACT HERE. DO NOT "FIX" IT. ═══════════════════
 *
 * `apiRequest` throws `new Error(<caller-supplied literal>)` on any non-OK response and **discards
 * the server's body entirely** — status code, `{ error }` string, `details`, everything. That is
 * `use-database.ts:26` and its twenty siblings, verbatim. The consequence is the defect the owner
 * wants to see on screen: with an expired JWT the Worker answers `403 {"error":"Invalid token."}`,
 * the query rejects, `EnhancedSavedDocuments` never reads `isError`, and the sidebar renders
 * **"No documents in this folder"** — a server outage is indistinguishable from an empty account.
 *
 * Consequently this module must NOT grow:
 *   - no retry logic (React Query's own `retry: 3` default applies because `App.tsx` constructs
 *     `new QueryClient()` with no options — spec §1.2; leave that alone too),
 *   - no global 401/403 interceptor, no token-clearing on 403, no redirect to the login screen
 *     ("The frontend has **no** 401/403 interceptor anywhere" — spec §2.4),
 *   - no `response.status` on the thrown Error, no error normalisation, no `cause`,
 *   - no timeout, no `AbortSignal`, no request logging.
 *
 * The one exception is `readErrorBody`, which exists solely because `useDeleteFolder` alone parses
 * `error.error` (`use-database.ts:421-424`). It is a faithful asymmetry, not a starting point.
 */
import type { ApiErrorBody, User } from './types'
import { getHexclaveToken } from './hexclave'

// ---------------------------------------------------------------------------
// Base URL — `src/lib/api-config.ts:4,7`
// ---------------------------------------------------------------------------

/**
 * `import.meta.env.VITE_API_URL` typed at the boundary. Vite declares `ImportMetaEnv` with a
 * `[key: string]: any` index signature, so annotating here is what keeps `any` out of the module.
 */
const VITE_API_URL: string | undefined = import.meta.env.VITE_API_URL

/**
 * Source: `API_BASE_URL = import.meta.env.VITE_API_URL || 'https://latex2html-backend.onrender.com'`
 * (`api-config.ts:4`).
 *
 * ⚠️ **The `''` fallback is for DEV ONLY, and the comment here used to claim otherwise.**
 *
 * It said "on Cloudflare the Worker is same-origin with the Pages deployment". **It is not.** Pages
 * serves `l2hc.pages.dev`, the Worker answers on `l2hc-api.comptes-ff0.workers.dev`, and there is no
 * `/api` route on the Pages domain. With `VITE_API_URL` unset in a production build, every request
 * goes to `https://l2hc.pages.dev/api/…`, hits the SPA fallback, and comes back as **index.html with
 * status 200** — so `response.ok` is true, `response.json()` throws, and `getCurrentUser` reports it
 * as "signed out" (DF-22 rank 5 swallows it). The app breaks in a way that points nowhere near here.
 *
 * That shipped on 2026-08-09 and was caught only by curling the path the *browser* takes. Testing
 * the Worker at its own origin passes regardless, which is why it was missed.
 *
 * **Production now gets the value from the committed `.env.production`**, which `vite build` loads
 * automatically — not from a variable somebody remembers to pass. Read that file before changing
 * anything here.
 *
 * In dev the fallback is correct: `VITE_API_URL` is unset, so paths stay relative and
 * `vite.config.ts` proxies `/api` → `http://127.0.0.1:8787` (`wrangler dev`). Spec §2.3 asks that
 * `apiUrl()` stay the single indirection point; it does.
 */
export const API_BASE_URL: string = VITE_API_URL || ''

/** `apiUrl(path) = ${API_BASE_URL}${path}` — `api-config.ts:7`, unchanged. */
export const apiUrl = (path: string): string => `${API_BASE_URL}${path}`

// ---------------------------------------------------------------------------
// Auth headers — `use-database.ts:8-14` === `use-admin.ts:9-15`
// ---------------------------------------------------------------------------

/**
 * ═══ THE localStorage TOKEN IS GONE ════════════════════════════════════════════════════════════
 * `AUTH_TOKEN_KEY`, `authStorage` and the synchronous `getAuthHeaders()` were deleted on 2026-08-09
 * with the legacy login (plan §10 step 3). The SDK owns the session now, in its own cookie on the
 * Pages origin, and it refreshes the access token silently — so there is nothing for the app to
 * store and nothing for it to clear.
 *
 * Two reproduced quirks died with them, both recorded in plan §9 change 6 rather than lost:
 *   - the **empty `Authorization:` header when logged out** (`token ? \`Bearer ${token}\` : ''`),
 *     which the Worker answered `401 Access denied. No token provided.`;
 *   - `Content-Type: application/json` on GET and DELETE, which carry no body. That one survives
 *     below, because it is still what the source sent on every data request.
 */

/**
 * The headers every authenticated request carries.
 *
 * **Why it is async, and why that is not negotiable.** A Hexclave access token lives ~10 minutes and
 * the SDK refreshes it silently in the background, so there is no correct *synchronous* answer to
 * "what is the current token?" — whatever is in storage may have expired a second ago.
 * `getAccessToken()` returns a promise, so this seam is a promise too, and `apiRequest` awaits it.
 *
 * Sends the raw JWT as `x-stack-access-token`, and **omits the header entirely** when signed out —
 * where the deleted legacy path sent an empty `Authorization:` (plan §9 change 6). The Worker
 * answers the same `401 Access denied. No token provided.` either way, so nothing downstream moved.
 */
export async function getAuthHeadersAsync(): Promise<Record<string, string>> {
  const token = await getHexclaveToken()
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'x-stack-access-token': token } : {}),
  }
}

// ---------------------------------------------------------------------------
// The request wrapper
// ---------------------------------------------------------------------------

export interface ApiRequestOptions {
  /**
   * Omitted for GET, exactly as the source omitted the key (`use-database.ts:23-25`).
   *
   * `PATCH` is **added**, not ported — the source had no endpoint using it. It arrived with the
   * admin profile and role endpoints (2026-08-09). Adding a verb to this union changes nothing for
   * the ported call sites: every one of them still sends exactly the method it always did.
   */
  method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** `JSON.stringify`d when present. Omitted entirely for GET/DELETE and for the admin approve PUT. */
  body?: unknown
  /**
   * The literal thrown on a non-OK response, e.g. `'Failed to fetch documents'`. The server's
   * response body is discarded in its place — DF-22 rank 5. Pass the source's exact string.
   */
  errorMessage: string
  /**
   * DF-22 rank 5, the sole exception. `useDeleteFolder` (`use-database.ts:421-424`) reads the
   * response body and throws `error.error || 'Failed to delete folder'`. Every other call site in
   * the app discards it. Do not set this anywhere else.
   *
   * Note the source calls `await response.json()` unguarded here, so a non-JSON error page rejects
   * with a `SyntaxError` whose message reaches the toast instead. Reproduced.
   */
  readErrorBody?: boolean
}

/**
 * The shape every hook in `use-database.ts` repeated twenty-two times:
 *
 * ```ts
 * const response = await fetch(apiUrl('/api/documents'), { headers: getAuthHeaders() });
 * if (!response.ok) throw new Error('Failed to fetch documents');
 * return response.json();
 * ```
 *
 * `response.json()` is called unguarded on success too. That matters on two documented paths where
 * the Worker answers 200 with an **empty body** — `PUT /api/documents/:id` and
 * `PUT /api/folders/:id` when the row is deleted between the existence probe and the UPDATE
 * (`worker/routes/documents.ts:414-416`, `worker/routes/folders.ts:315-327`). The parse rejects
 * with a `SyntaxError` and the mutation's `onError` toasts it. The source did the same. Preserved.
 */
export async function apiRequest<T>(path: string, options: ApiRequestOptions): Promise<T> {
  // `await`ed because a Hexclave token is fetched asynchronously (see getAuthHeadersAsync). In
  // legacy mode this resolves immediately with the identical synchronous result, so the request the
  // Worker sees is unchanged.
  const init: RequestInit = { headers: await getAuthHeadersAsync() }
  if (options.method !== undefined) init.method = options.method
  if (options.body !== undefined) init.body = JSON.stringify(options.body)

  const response = await fetch(apiUrl(path), init)

  if (!response.ok) {
    if (options.readErrorBody === true) {
      const error = (await response.json()) as ApiErrorBody
      throw new Error(error.error || options.errorMessage)
    }
    // DF-22 rank 5 — the body is read by nobody and dropped on the floor.
    throw new Error(options.errorMessage)
  }

  return (await response.json()) as T
}

// ---------------------------------------------------------------------------
// Auth endpoints — `src/lib/auth.ts:15-67`
// ---------------------------------------------------------------------------

/**
 * One auth call. `register` and `login` were deleted with the Worker routes they targeted
 * (2026-08-09, plan §10 step 3) — Hexclave's hosted pages own sign-up, sign-in, password reset and
 * email verification now.
 *
 * `getCurrentUser` does NOT go through {@link apiRequest}: it swallows more than anything else in
 * the app — a non-OK response returns `null`, and a thrown network error is caught, logged and also
 * returns `null` (`auth.ts:49-66`). It also sends no `Content-Type`, having no body.
 */
export const authApi = {
  /**
   * `GET /api/auth/me` — `auth.ts:49-66`.
   *
   * Returns `null` on **any** failure: 401 (no token / bad token), **403 (unapproved)**, a 500, or
   * the network being down. `HexclaveAuthProvider` therefore reads all of those as "no D1 user",
   * which is what puts an unapproved translator on `HexclaveAuthForm`'s pending screen — and also
   * what makes that screen unable to tell an unapproved account from a Worker outage. Part of DF-22
   * rank 5's family; reproduced, not repaired (CLAUDE.md §3).
   *
   * ═══ THE HEADER (plan §7.0) ══════════════════════════════════════════════════════════════════
   * **`x-stack-access-token`, carrying the raw JWT** — the vendor's own header for JWKS-verified
   * backends, and what `extractHexclaveToken` reads. Not `Authorization`, and specifically not
   * `getAuthorizationHeader()`, which returns `Bearer stackauth_eyJ…`; `jose` cannot verify that,
   * and it fails as a *signature* error that reads like a JWKS problem and is not.
   *
   * Still no `Content-Type` — it has no body.
   */
  async getCurrentUser(token: string): Promise<User | null> {
    try {
      const response = await fetch(apiUrl('/api/auth/me'), {
        headers: { 'x-stack-access-token': token },
      })

      if (!response.ok) {
        return null
      }

      return (await response.json()) as User
    } catch (error) {
      console.error('Failed to get current user:', error)
      return null
    }
  },
}
